/**
 * Feature Carousel (E20-1071)
 *
 * A heading row with prev/next arrow controls above a horizontally scrolling
 * row of feature cards. Navigation: arrow buttons (page by ~one viewport of
 * cards), pagination dots (mobile), native touch swipe, and mouse/pen
 * drag-to-scroll on desktop. Arrows and dots both land a card flush to the
 * content-container edge (CSS scroll-snap + scroll-padding keep manual swipes
 * consistent). Arrow controls disable (25% opacity via CSS) at each end; the
 * active dot tracks the in-view card. The section fades in once CompareHeights
 * has equalized card heights. Reduced-motion aware.
 */
class FeatureCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.cards = Array.from(this.querySelectorAll('.feature-carousel__card'));
    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    if (!this.track) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.ticking = false;
    this.isDown = false;

    this.prev?.addEventListener('click', () => this.page(-1), { signal });
    this.next?.addEventListener('click', () => this.page(1), { signal });
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.scrollToCard(index), { signal });
    });

    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
    window.addEventListener('resize', () => this.update(), { passive: true, signal });

    // Mouse / pen drag-to-scroll (touch uses native scrolling).
    this.track.addEventListener('pointerdown', (e) => this.onPointerDown(e), { signal });
    this.track.addEventListener('pointermove', (e) => this.onPointerMove(e), { signal });
    this.track.addEventListener('pointerup', (e) => this.onPointerUp(e), { signal });
    this.track.addEventListener('pointercancel', (e) => this.onPointerUp(e), { signal });
    // Suppress a card link click that's really the end of a drag.
    this.track.addEventListener(
      'click',
      (e) => {
        if (this.dragged) {
          e.preventDefault();
          e.stopPropagation();
          this.dragged = false; // only block the click immediately after a drag
        }
      },
      { capture: true, signal }
    );

    // Reveal once card heights are equalized (CSS reveals as a fallback too).
    this.addEventListener('compare-heights:aligned', () => this.reveal(), { signal });
    this.revealTimer = setTimeout(() => this.reveal(), 1000);

    this.update();
  }

  reveal() {
    this.classList.add('is-ready');
    clearTimeout(this.revealTimer);
  }

  reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // The content-container inset (track's start padding) that cards align to.
  gutter() {
    return parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
  }

  cardsPerView() {
    return Math.max(1, Math.floor(this.track.clientWidth / this.cardOuterWidth()));
  }

  // One card's width plus the row gap. Cards are uniform, so a single measurement
  // is enough — avoids a per-card layout read on every scroll tick.
  cardOuterWidth() {
    const card = this.cards[0];
    if (!card) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  // Index of the card currently aligned to the start edge. Snapped scrollLeft for
  // card i is i * cardOuterWidth, so derive it arithmetically and clamp at the
  // ends (the last card can't scroll fully to the left edge).
  activeIndex() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    if (scrollLeft <= 1) return 0;
    if (scrollLeft >= scrollWidth - clientWidth - 1) return this.cards.length - 1;

    const cardOuter = this.cardOuterWidth();
    if (cardOuter <= 0) return 0;
    const index = Math.round(scrollLeft / cardOuter);
    return Math.min(this.cards.length - 1, Math.max(0, index));
  }

  page(direction) {
    // Desktop scrolls freely by ~one viewport (smooth, no snap); mobile snaps to
    // a whole-card boundary so cards land flush to the content container.
    if (window.matchMedia('(min-width: 769px)').matches) {
      this.track.scrollBy({
        left: this.track.clientWidth * 0.9 * direction,
        behavior: this.reduceMotion() ? 'auto' : 'smooth',
      });
      return;
    }
    const target = this.activeIndex() + this.cardsPerView() * direction;
    this.scrollToCard(Math.min(this.cards.length - 1, Math.max(0, target)));
  }

  // Aligns a card's start edge to the content container (matches the snap point).
  scrollToCard(index) {
    const card = this.cards[index];
    if (!card) return;
    const target =
      card.getBoundingClientRect().left -
      this.track.getBoundingClientRect().left +
      this.track.scrollLeft -
      this.gutter();
    this.track.scrollTo({ left: target, behavior: this.reduceMotion() ? 'auto' : 'smooth' });
  }

  onPointerDown(e) {
    this.dragged = false;
    // Touch uses native scrolling; only the primary (left) button drags.
    if (e.pointerType === 'touch' || e.button !== 0) return;
    this.isDown = true;
    this.pointerId = e.pointerId;
    this.startX = e.clientX;
    this.startScroll = this.track.scrollLeft;
    // Capture is deferred to the first real drag (onPointerMove) — capturing on
    // pointerdown would redirect the click off links inside the track.
  }

  onPointerMove(e) {
    if (!this.isDown || e.pointerId !== this.pointerId) return;
    // Button released outside the track (before capture) — end the drag.
    if (e.buttons === 0) {
      this.onPointerUp(e);
      return;
    }
    const dx = e.clientX - this.startX;
    if (!this.dragged) {
      if (Math.abs(dx) <= 5) return; // not a drag yet — let clicks through to links
      this.dragged = true;
      this.track.setPointerCapture(this.pointerId);
      this.track.classList.add('is-dragging');
    }
    this.track.scrollLeft = this.startScroll - dx;
  }

  onPointerUp(e) {
    if (!this.isDown || e.pointerId !== this.pointerId) return;
    this.isDown = false;
    // Removing is-dragging re-enables scroll-snap, settling on the nearest card.
    this.track.classList.remove('is-dragging');
    try {
      this.track.releasePointerCapture(e.pointerId);
    } catch {
      // pointer may already be released or was never captured
    }
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  update() {
    this.updateControls();
    this.updateDots();
  }

  updateControls() {
    if (!this.prev || !this.next) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    this.prev.disabled = scrollLeft <= 1;
    this.next.disabled = scrollLeft >= scrollWidth - clientWidth - 1;
  }

  updateDots() {
    if (this.dots.length === 0) return;
    const active = this.activeIndex();
    this.dots.forEach((dot, i) => {
      const isActive = i === active;
      dot.classList.toggle('feature-carousel__dot--active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  disconnectedCallback() {
    clearTimeout(this.revealTimer);
    this.abortController?.abort();
  }
}

if (!customElements.get('feature-carousel')) {
  customElements.define('feature-carousel', FeatureCarousel);
}
